import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import moment from 'moment';
import { G2S } from 'core/g2s';
import router from 'src/router';
import * as api from 'core/api';
import * as flights from 'core/flights';
import * as utils from 'core/utils';
import FlightsSearchResultsItemComponent from './item';
import FlightsSearchResultsSidebarComponent from './sidebar';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

@WithRender
@Component({
    components: {
        'v-flights-search-results-item': FlightsSearchResultsItemComponent,
        'v-flights-search-results-sidebar': FlightsSearchResultsSidebarComponent
    }
})
export default class FlightsSearchResultsComponent extends Vue {
    @Prop()
    searchGuid: string; // used for /flights/archive

    @Prop()
    searchFrom: string;

    @Prop()
    searchFrom2: string;

    @Prop()
    searchTo: string;

    @Prop()
    searchTo2: string;

    @Prop()
    searchDate: string;

    @Prop()
    searchDate2: string;

    @Prop()
    searchPassengers: string;

    @Prop()
    searchClass: string;

    @Prop()
    searchFilters: string;

    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();

    gaAlreadySubmitted: string[] = [];

    trackParams: utils.TrackParams = new utils.TrackParams();

    view: string = 'loading';

    viewResults: string = 'list';
    viewResultsTransition: string = 'slide-right';

    searchError: string | null = null;

    searchParams: flights.FlightSearchParams = {
        from: '',
        to: '',
        departure: null,
        return: null,
        roundtrip: false,
        class: flights.BookingClass.Economic,
        passengers: {
            adults: 0,
            children: 0,
            infants: 0
        }
    };

    filters: flights.FlightFilterService | null = null;
    filteringInProgress: boolean = false;

    flightsToFilter: flights.FlightFilterModel[] = [];
    flightsToView: { [uniqueId: string]: flights.FlightModel } | undefined = undefined; // Force non-reactive
    flightsVisibleCount: number = 0;
    flightsVisibleParentCount: number = 0;

    resultsBanners: api.Banner[] = [];
    resultsPageSize: number = 20;
    resultsVisible: number = this.resultsPageSize;

    searchId: string | null = null;

    selectedFlightUniqueId: string = '';
    selectedParentFlightUniqueId: string = '';

    lastHistoryStateView: string = '';

    loadingBanners: api.Banner[] = [];

    megoPopupSearchIsVisible: boolean = false;

    cheapestFlight: flights.FlightModel | {} = {};
    fasterFlight: flights.FlightModel | {} = {};
    bestFlight: flights.FlightModel | {} = {};

    get filteredSearchResults(): flights.FlightFilterModel[] {
        if (this.flightsToFilter && this.filters) {
            this.filteringInProgress = true;

            const r = this.filters.filter(this.flightsToFilter, this.flightsToView!, this.filters, this.searchParams.roundtrip && this.flightsToFilter.length > 5);

            this.flightsVisibleCount = r.flightsVisibleCount;
            this.flightsVisibleParentCount = r.flightsVisibleParentCount;

            return this.flightsToFilter.filter(f => {
                return f.visible && !f.parentId;
            });
        } else {
            return [];
        }
    }

    get pagedSearchResults(): flights.FlightFilterModel[] {
        this.$nextTick(() => {
            this.filteringInProgress = false;
        });

        return this.filteredSearchResults.slice(0, this.resultsVisible);
    }

    get selectedFlight(): flights.FlightModel {
        return this.flightsToView![this.selectedFlightUniqueId];
    }

    get selectedParentFlight(): flights.FlightModel {
        return this.flightsToView![this.selectedParentFlightUniqueId];
    }

    get totalPassengers() {
        return this.searchParams.passengers.adults
            + this.searchParams.passengers.children
            + this.searchParams.passengers.infants;
    }

    get flightsVisibleCountLabel(): string {
        if (this.filters && this.filters.touched) {
            return i18next.t('numerous_flights_filtered', <any>{count: this.flightsVisibleCount});
        } else {
            return i18next.t('numerous_flights_found', <any>{count: this.flightsVisibleCount});
        }
    }

    onSameSearch() {
        this.init();
    }

    onSearch() {
        this.$nextTick(() => {
            window.history.replaceState({ ...window.history.state, view: 'list' }, '', router.currentRoute.fullPath);
        });
    }

    bookingUrl(flightUniqueId) {
        return new flights.FlightNavigationService().getBookingUrl(this.searchParams, this.searchId!, flightUniqueId);
    }

    getMoreLabel(): string {
        const b = i18next.t('numerous_flights', { count: this.resultsPageSize });
        return i18next.t('label_show_more_withvalue', <any>{ i: b });
    }

    @Watch('$route')
    onRouteChanged() {
        this.loadModel();
    }

    updated(){
        if(this.filteredSearchResults.length !== 0){
            this.cheapestFlight = this.getCheapestFlight(this.filteredSearchResults);
            this.fasterFlight = this.getFasterFlight(this.filteredSearchResults);
            this.bestFlight = this.getBestFlight(this.filteredSearchResults);        
        }
    }

    mounted() {
        window.history.replaceState({ ...window.history.state, view: 'list' }, '', router.currentRoute.fullPath);
        this.setLastHistoryStateView();
        window.addEventListener('popstate', this.onPopState);

        this.init();
    }

    beforeDestroy() {
        window.removeEventListener('popstate', this.onPopState);
    }

    setLastHistoryStateView() {
        this.lastHistoryStateView = window.history.state && window.history.state.view ? window.history.state.view : '';
    }

    onPopState(event) {
        if (event.state && event.state.view == 'flight' && this.lastHistoryStateView == 'list') {
            this.onFlightDetails();
        } else
        if (event.state && event.state.view == 'list' && this.lastHistoryStateView == 'flight') {
            this.onFlightDetailsBack('list');
        } else
        if (event.state && event.state.view == 'flightGroup' && this.lastHistoryStateView == 'list') {
            this.onFlightGroupDetails();
        } else
        if (event.state && event.state.view == 'flight' && this.lastHistoryStateView == 'flightGroup') {
            this.onFlightDetails();
        } else
        if (event.state && event.state.view == 'flightGroup' && this.lastHistoryStateView == 'flight') {
            this.onFlightDetailsBack('flightGroup');
        } else
        if (event.state && event.state.view == 'list' && this.lastHistoryStateView == 'flightGroup') {
            this.onFlightGroupDetailsBack();
        }
    }

    init() {
        // Redirect to mobile
        if (G2S.config.mobileRedirectAllowed) {
            window.location.href = G2S.config.mobileUrl
                + this.$route.path + (window.location.search && window.location.search.length > 1 ? window.location.search : '');
            return;
        }

        this.loadModel();
    }

    initViewLoading() {
        this.loadingBanners = [];

        const bannerService = new api.BannerService();

        const request: api.BannerRequest = {
            displayArea: 'FlightsSearchInProgress',
            departureDestinationId: undefined,
            arrivalDestinationId: undefined,
            validatingAirlineId: undefined
        };

        bannerService.list(request).then(
            (result: api.Banner[]) => {
                if (result && result.length > 0) {
                    this.loadingBanners = result;
                }
            }
        );
    }

    loadModel() {
        if (this.searchFrom2 && this.searchTo2 && this.searchDate2) {
            this.searchParams = new flights.FlightNavigationService().getRoundtripParams(
                this.searchFrom,
                this.searchTo,
                this.searchDate,
                this.searchFrom2,
                this.searchTo2,
                this.searchDate2,
                this.searchPassengers,
                this.searchClass
            )!;
        }
        else {
            this.searchParams = new flights.FlightNavigationService().getOnewayParams(
                this.searchFrom,
                this.searchTo,
                this.searchDate,
                this.searchPassengers,
                this.searchClass
            )!;
        }

        this.view = 'loading';
        this.viewResults = 'list';

        if (this.searchParams) {
            this.loadSearchNew();
        }
        else {
            if (this.searchGuid) {
                this.loadSearchArchive();
            }
            else {
                location.href = '/';
                return;
            }
        }        
    }

    loadSearchNew() {
        const flightSearchService = new flights.FlightSearchService();

        // Init GA params
        (<any>this).$ga.addSearchParams(this.gaParams, this.searchParams);

        (<any>this).$ga.sendPageview('/flights/search/start', 'Flights / Search / Start', this.gaParams);

        // Tracking
        utils.TrackingService.updateParamsFromSearch(this.trackParams, this.searchParams);
        utils.TrackingService.track(
            utils.TrackEvent.FlightSearchStart,
            this.trackParams
        );

        document.getElementById('pixels')!.innerHTML = '';
        utils.PixelsService.setPixelForFlightSearch(document.getElementById('pixels')!, this.searchParams);

        this.initViewLoading();

        flightSearchService.addHistoryItem(this.searchParams);

        flightSearchService.search(this.searchParams).then(
            (result: flights.FlightSearchResult) => {
                new api.RetailRocket().searchCompleted({
                    searchId: result.searchId,
                    searchPhrase: router.currentRoute.fullPath.replace('/flights/', ''),
                    mailTrackingId: <any>this.$route.query.MailTrackingId
                });
                
                this.gaParams.metric1 = result.duration;

                if (result && result.flights && result.flights.length > 0) {
                    this.searchId = result.searchId;
                    this.resultsBanners = result.banners;
                    this.flightsToFilter = result.flightsToFilter;
                    this.filters = new flights.FlightFilterService(result.flights, this.searchFilters);
                    this.flightsToView = {};

                    result.flights.forEach(f => {
                        this.flightsToView![f.uniqueId] = f;
                    });

                    this.flightsVisibleCount = this.flightsToFilter.length;
                    this.flightsVisibleParentCount = this.flightsVisibleCount;

                    this.view = 'results';

                    this.gaParams.metric2 = result.flights.length;

                    (<any>this).$ga.sendPageview('/flights/search/results', 'Flights / Search / Results', this.gaParams);

                    utils.TrackingService.track(
                        utils.TrackEvent.FlightSearchResults,
                        this.trackParams,
                        {
                            FlightCount: result.flights.length
                        }
                    );
                } else {
                    this.view = 'empty';

                    (<any>this).$ga.sendEvent('flights.results', 'msg.noresults', 'flights.results.msg.noresults', this.gaParams);

                    utils.TrackingService.track(
                        utils.TrackEvent.FlightSearchEmpty,
                        this.trackParams
                    );
                }
            },
            (error) => {
                this.searchError = i18next.t('error_server_tryagain');

                if (error.response) {
                    if (error.response.status === 400) {
                        this.searchError = i18next.t('error_search_wrong_params');
                    }
                    console.log('XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('XHR error', error.message);
                }

                this.view = 'error';

                (<any>this).$ga.sendEvent('flights.results', 'msg.error', 'flights.results.msg.error', this.gaParams);

                utils.TrackingService.track(
                    utils.TrackEvent.FlightSearchError,
                    this.trackParams
                );
            }
        );
    }

    loadSearchArchive() {
        const fightNavigationService = new flights.FlightNavigationService();
        const flightSearchService = new flights.FlightSearchService();
        const flightService = new api.FlightService();

        flightService.searchArchive({ searchId: this.searchGuid }).then(
            (archiveResult: api.FlightSearchArchiveResult) => {
                this.searchParams = fightNavigationService.getParamsFromRequest(archiveResult.searchRequest);

                const result = flightSearchService.processResults(this.searchParams, archiveResult);

                if (result && result.flights && result.flights.length > 0) {
                    this.searchId = result.searchId;
                    this.resultsBanners = result.banners;
                    this.flightsToFilter = result.flightsToFilter;
                    this.filters = new flights.FlightFilterService(result.flights, this.searchFilters);
                    this.flightsToView = {};

                    result.flights.forEach(f => {
                        this.flightsToView![f.uniqueId] = f;
                    });

                    this.flightsVisibleCount = this.flightsToFilter.length;
                    this.flightsVisibleParentCount = this.flightsVisibleCount;

                    this.view = 'results';
                } else {
                    this.view = 'empty';
                }
            },
            (error) => {
                this.searchError = i18next.t('error_server_tryagain');

                if (error.response) {
                    if (error.response.status === 400) {
                        this.searchError = i18next.t('error_search_wrong_params');
                    }
                    console.log('XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('XHR error', error.message);
                }

                this.view = 'error';
            }
        );
    }

    getNextDaySearchUrl(): string {
        const searchParams: flights.FlightSearchParams = {
            from: this.searchParams.from,
            to: this.searchParams.to,
            departure: this.searchParams.departure,
            return: this.searchParams.return,
            roundtrip: this.searchParams.roundtrip,
            class: this.searchParams.class,
            passengers: this.searchParams.passengers
        };

        const d = moment(searchParams.departure!).add(1, 'days');

        searchParams.departure = d.toDate();

        if (searchParams.return) {
            searchParams.return = d.add(1, 'days').toDate();
        }

        return new flights.FlightNavigationService().getSearchResultsUrl(searchParams);
    }

    getPrevDaySearchUrl(): string | null {
        const now = moment();
        const d = moment(this.searchParams.departure!);

        if (d.isAfter(now, 'day')) {
            const searchParams: flights.FlightSearchParams = {
                from: this.searchParams.from,
                to: this.searchParams.to,
                departure: d.add(-1, 'days').toDate(),
                return: this.searchParams.return ? moment(this.searchParams.return).add(-1, 'days').toDate() : null,
                roundtrip: this.searchParams.roundtrip,
                class: this.searchParams.class,
                passengers: this.searchParams.passengers
            };

            return new flights.FlightNavigationService().getSearchResultsUrl(searchParams);
        }

        return null;
    }

    sumFast(flight: flights.FlightModel) {
        return flight.legs.reduce((total, f) => total + f.durationMinutes, 0) / 60;
    }
    
    getCheapestFlight(flightsForSort: flights.FlightFilterModel[]) {
        const flightsSortFast = [...flightsForSort].sort(
            utils
                .firstBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return (<any>this).flightsToView[v1.uniqueId].billingPrice - (<any>this).flightsToView[v2.uniqueId].billingPrice; })
                .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return this.sumFast((<any>this).flightsToView[v1.uniqueId]) - this.sumFast((<any>this).flightsToView[v2.uniqueId]); })
        );

        return (<any>this).flightsToView[flightsSortFast[0].uniqueId];
    }

    getFasterFlight(flightsForSort: flights.FlightFilterModel[]) {
        const flightsSortFast = [...flightsForSort].sort(
            utils
                .firstBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return this.sumFast((<any>this).flightsToView[v1.uniqueId]) - this.sumFast((<any>this).flightsToView[v2.uniqueId]); })
                .thenBy((v1: flights.FlightFilterModel, v2: flights.FlightFilterModel) => { return (<any>this).flightsToView[v1.uniqueId].billingPrice - (<any>this).flightsToView[v2.uniqueId].billingPrice; })
        );

        return (<any>this).flightsToView[flightsSortFast[0].uniqueId];
    }

    // Желательно переписать и отправить в core @todo
    getBestFlight(flightFilters: flights.FlightFilterModel[]) {
        const localFlightFilters = [...flightFilters];
        const resByTime = localFlightFilters.sort((a, b) => this.sumFast((<any>this).flightsToView[a.uniqueId]) - this.sumFast((<any>this).flightsToView[b.uniqueId])).map((e, i) => ({
            uid: e.uniqueId,
            index: i,
            value: this.sumFast((<any>this).flightsToView[e.uniqueId]),
            priority: (<any>this).flightsToView[e.uniqueId]
        }));
        
        const resByCost = localFlightFilters.sort((a, b) => (<any>this).flightsToView[a.uniqueId].billingPrice - (<any>this).flightsToView[b.uniqueId].billingPrice).map((e, i) => ({
            uid: e.uniqueId,
            index: i,
            value: (<any>this).flightsToView[e.uniqueId].billingPrice,
            priority: (<any>this).flightsToView[e.uniqueId]
        }));

        const swap = (json)=>{
            const ret = {};
            for(const key in json) {
                ret[json[key].uid] = json[key].rank;
            }
            return ret;
        };                
        
        const setRank = (elems) => {  
            let prevValue = null;
            let rankIndex = 1;
            
            for (const elem of elems) {
                if (!prevValue || elem.value == (<any>prevValue).value) {
                    elem.rank = rankIndex;
                } else {
                    elem.rank = ++rankIndex;
                }
                
                prevValue = elem;
                
            }
            
            return elems;
        };
        
        const merge = (time, cost) => {
            const res = {};
            
            for (const key in time) {
                res[key] = parseFloat(cost[key]) + parseFloat(time[key]) * 0.5;
            }
                
            return res;
        };
        
        const result = merge(
            swap(setRank(resByTime)),
            swap(setRank(resByCost))
        );

        return (<any>this).flightsToView[localFlightFilters.sort((a, b) => result[a.uniqueId] - result[b.uniqueId])[0].uniqueId];
        
    }

    onFlightDetails(flight?: flights.FlightModel) {
        this.setLastHistoryStateView();

        if (flight) {
            this.selectedFlightUniqueId = flight.uniqueId;
        }

        this.viewResultsStepForward('flight');

        // Track add to cart
        (<any>this).$ga.addFlightProduct(this.selectedFlight, this.totalPassengers);
        (<any>this).$ga.setAction('add', null);

    }

    onFlightDetailsClick(flight: flights.FlightModel) {
        (<any>this).$ga.sendEvent('flights.results', 'flight.details', 'flights.results.flight.details', this.gaParams);

        window.history.pushState({ ...window.history.state, view: 'flight' }, '', router.currentRoute.fullPath);
        this.onFlightDetails(flight);
    }

    onFlightDetailsBack(to: string) {
        this.setLastHistoryStateView();

        if (to == 'flightGroup') {
            this.viewResultsStepBack('flightGroup');

            this.$nextTick(function () {
                document.querySelector('#f' + this.selectedParentFlightUniqueId)!.scrollIntoView();
            });
        } else if (to == 'list') {
            this.viewResultsStepBack('list');

            this.$nextTick(function () {
                document.querySelector('#fg' + this.selectedFlightUniqueId)!.scrollIntoView();
            });
        }
    }

    onFlightDetailsBackClick() {
        window.history.go(-1);
    }

    onFlightGroupDetails(flight?: flights.FlightModel) {
        this.setLastHistoryStateView();

        if (flight) {
            this.selectedParentFlightUniqueId = flight.uniqueId;
        }

        this.viewResultsStepForward('flightGroup');

        (<any>this).$ga.sendEvent('flights.results', 'flight.group.details', 'flights.results.flight.group.details', this.gaParams);
    }

    onFlightGroupDetailsClick(flight: flights.FlightModel) {
        window.history.pushState({ ...window.history.state, view: 'flightGroup' }, '', router.currentRoute.fullPath);
        this.onFlightGroupDetails(flight);
    }

    onFlightGroupDetailsBack() {
        this.setLastHistoryStateView();

        this.viewResultsStepBack('list');

        this.$nextTick(function () {
            document.querySelector('#fg' + this.selectedParentFlightUniqueId)!.scrollIntoView();
        });
    }

    onFlightGroupDetailsBackClick() {
        window.history.go(-1);
    }

    onFlightTerms(flightUniqueId: string) {
        (<any>this).$refs.flightTerms.show(this.searchId, flightUniqueId);
    }

    resetFilters(scrollTop: boolean) {
        this.filters!.clear();

        if (scrollTop) {
            window.scrollTo(0, 0);
        }
    }

    scrollTop() {
        window.scrollTo(0, 0);
    }

    setView(newView: string) {
        this.view = newView;
        window.scrollTo(0, 0);

        if (newView === 'process-booking') {
            window.setTimeout(() => { this.view = 'services'; }, 3000);
        }

        if (newView === 'process-payment') {
            window.setTimeout(() => { this.view = 'confirmation'; }, 3000);
        }
    }

    viewResultsStepBack(targetState: string) {
        this.viewResultsTransition = 'slide-left';
        this.viewResults = targetState;
    }

    viewResultsStepForward(targetState: string) {
        this.viewResultsTransition = 'slide-right';
        this.viewResults = targetState;

        this.$nextTick(function () {
            utils.ScrollTo.top();
        });
    }

    showMegoPopupSearch() {
        this.megoPopupSearchIsVisible = true;
    }

    hideMegoPopupSearch() {
        this.megoPopupSearchIsVisible = false;
    }
}
